/**
 * Thin fetch wrapper. Always credentials: 'include' — server uses cookie sessions.
 * Mutating requests must carry the CSRF token, fetched lazily via /api/csrf-token.
 *
 * Session-expiry handling — reactive pattern (no heartbeat, no polling).
 * The wrapper invokes `api.onSessionExpired(message)` callback the very
 * moment any protected endpoint returns 401. App.jsx registers the
 * callback once at mount → setUser(null) + toast. Reference pattern
 * lives in manuals/COP ADMS session-expiry-autologout-prompt.md.
 *
 * /api/auth/me and /api/auth/login are excluded from the auto-expel —
 * those are SUPPOSED to 401 (the bootstrap probe + bad password flow).
 */

let csrfToken = null;

// Endpoints where 401 is a NORMAL outcome (probing "am I logged in?" or
// trying to log in with wrong credentials). For these we never fire the
// session-expired callback — the caller handles the response inline.
const AUTH_PROBE_PATHS = new Set([
  '/api/auth/me',
  '/api/auth/login',
]);

function notifyAuthExpired(url) {
  // Drop the CSRF token — next session will need a fresh one.
  csrfToken = null;
  try {
    if (typeof api.onSessionExpired === 'function') {
      api.onSessionExpired('Сессия завершена. Войдите снова.', url);
    }
  } catch {
    /* callback threw — never let it crash the request pipeline */
  }
}

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  const res = await fetch('/api/csrf-token', { credentials: 'include' });
  if (!res.ok) throw new ApiError('failed to obtain csrf token', res.status);
  const body = await res.json();
  csrfToken = body.token;
  return csrfToken;
}

export class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function attempt(method, url, body) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const isMutation = method !== 'GET' && method !== 'HEAD';
  if (isMutation) {
    headers['x-csrf-token'] = await ensureCsrfToken();
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    // 2026-06-03 — force-bypass HTTP cache for every authenticated API
    // call. Without this, the browser BFCache (back/forward cache) and
    // memory cache may serve a STALE 200 response to GET /api/auth/me
    // immediately after F5 / Ctrl+F5, making the SPA think the session
    // is still alive when the server-side cookie has actually expired.
    // The operator then types data into a form and Save fails with 401,
    // losing the edits. `cache: 'no-store'` guarantees the request hits
    // the server every time so the real auth state is observed.
    cache: 'no-store',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let payload = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    payload = await res.json().catch(() => null);
  }
  return { res, payload };
}

async function request(method, url, body) {
  let { res, payload } = await attempt(method, url, body);

  // Retry-once on stale CSRF token (e.g. after server restart).
  if (
    !res.ok &&
    res.status === 403 &&
    payload?.error === 'invalid csrf token' &&
    method !== 'GET' &&
    method !== 'HEAD'
  ) {
    csrfToken = null;
    ({ res, payload } = await attempt(method, url, body));
  }

  if (!res.ok) {
    // Session expired or cookie invalidated server-side — boot the user
    // out of the SPA back to LoginPage. Skip for the two auth-probe
    // endpoints (see AUTH_PROBE_PATHS rationale at the top) where a 401
    // is part of the normal contract.
    //
    // Strip any query-string before comparing — the probes never carry
    // one but we don't want a future caller passing ?foo=bar to
    // accidentally bypass the skiplist.
    if (res.status === 401) {
      const pathOnly = url.split('?')[0];
      if (!AUTH_PROBE_PATHS.has(pathOnly)) {
        notifyAuthExpired(url);
      }
    }
    const message = payload?.error || res.statusText || 'request failed';
    throw new ApiError(message, res.status, payload);
  }
  return payload;
}

export const api = {
  // Reactive session-expiry hook. App.jsx assigns this once at mount
  // to a function (msg, url) => setUser(null) + toast. `notifyAuthExpired`
  // (above) calls it on every 401 from a non-auth endpoint. Null = no
  // handler registered → 401 just throws ApiError to the caller.
  onSessionExpired: null,

  health: () => request('GET', '/api/health'),

  me: () => request('GET', '/api/auth/me'),

  login: (username, password) => request('POST', '/api/auth/login', { username, password }),

  logout: () => request('POST', '/api/auth/logout'),

  changeOwnPassword: (currentPassword, newPassword) =>
    request('POST', '/api/auth/change-password', { currentPassword, newPassword }),

  users: {
    list:    ()                  => request('GET',    '/api/users'),
    get:     (id)                => request('GET',    `/api/users/${id}`),
    create:  (payload)           => request('POST',   '/api/users', payload),
    update:  (id, patch)         => request('PATCH',  `/api/users/${id}`, patch),
    remove:  (id)                => request('DELETE', `/api/users/${id}`),
    setPassword: (id, password)  => request('POST',   `/api/users/${id}/password`, { password }),
  },

  settings: {
    public: ()       => request('GET',   '/api/settings/public'),
    info:   ()       => request('GET',   '/api/settings/info'),
    system: ()       => request('GET',   '/api/settings/system'),
    updateSystem: (patch) => request('PATCH', '/api/settings/system', patch),
  },

  torBridges: {
    list:       ()                => request('GET',    '/api/tor-bridges'),
    create:     (payload)         => request('POST',   '/api/tor-bridges', payload),
    update:     (id, patch)       => request('PATCH',  `/api/tor-bridges/${id}`, patch),
    remove:     (id)              => request('DELETE', `/api/tor-bridges/${id}`),
    // Round 61 — reachability checks. Single-bridge check returns
    // { result: { id, kind, alive, message, latencyMs, enabled,
    //             wasEnabled } } and updates the row's enabled flag
    // to match the probe outcome. Batch check accepts optional
    // { kind } body — omit it to probe everything.
    check:      (id)              => request('POST',   `/api/tor-bridges/${id}/check`),
    checkBatch: (kind)            => request('POST',   '/api/tor-bridges/check', { kind }),
    // Round 62b — bulk flip enabled flag for an entire kind. One
    // policy bump for the whole batch.
    bulkEnable: (kind, enabled)   => request('POST',   '/api/tor-bridges/bulk-enable', { kind, enabled }),
    // Round 64e — wipe every row of a given kind in one transaction.
    // Loud destructive op — UI must confirm before calling.
    bulkDelete: (kind)            => request('POST',   '/api/tor-bridges/bulk-delete', { kind }),
    // 2026-06-10 — wipe only the disabled rows of a given kind. Sweep
    // dead endpoints after a batch-check has flagged them.
    bulkDeleteInactive: (kind)    => request('POST',   '/api/tor-bridges/bulk-delete-inactive', { kind }),
  },

  // Round 63 — operator-managed cron schedules.
  scheduledTasks: {
    list:   (category)        => request('GET',    `/api/scheduled-tasks${category ? `?category=${encodeURIComponent(category)}` : ''}`),
    create: (payload)         => request('POST',   '/api/scheduled-tasks', payload),
    update: (id, patch)       => request('PATCH',  `/api/scheduled-tasks/${id}`, patch),
    remove: (id)              => request('DELETE', `/api/scheduled-tasks/${id}`),
    run:    (id)              => request('POST',   `/api/scheduled-tasks/${id}/run`),
  },

  // Round 62 — bridge sources (public URLs that publish bridge lines).
  torBridgeSources: {
    list:     ()              => request('GET',    '/api/tor-bridge-sources'),
    create:   (payload)       => request('POST',   '/api/tor-bridge-sources', payload),
    update:   (id, patch)     => request('PATCH',  `/api/tor-bridge-sources/${id}`, patch),
    remove:   (id)            => request('DELETE', `/api/tor-bridge-sources/${id}`),
    fetch:    (id)            => request('POST',   `/api/tor-bridge-sources/${id}/fetch`),
    fetchAll: ()              => request('POST',   '/api/tor-bridge-sources/fetch-all'),
  },

  logs: {
    files: ()                            => request('GET',  '/api/logs/files'),
    app:   (params = {})                 => request('GET',  withQuery('/api/logs/app', params)),
    http:  (params = {})                 => request('GET',  withQuery('/api/logs/http', params)),
    clear: (file)                        => request('POST', '/api/logs/clear', { file }),
  },

  audit: {
    eventTypes: ()                       => request('GET',  '/api/audit/event-types'),
    list: (params = {})                  => request('GET',  withQuery('/api/audit', params)),
    clear: ()                            => request('POST', '/api/audit/clear'),
  },

  dashboard: {
    summary: ()                          => request('GET',  '/api/dashboard'),
  },

  license: {
    // Cheap polled snapshot — used by the global banner and Settings widget.
    status:  ()                          => request('GET',  '/api/license/status'),
    // Force one-shot poll outside the normal cadence (admin only).
    refresh: ()                          => request('POST', '/api/license/refresh'),
    // Heavy — pulls full Token + trusted_key_role + vCPU. Call once on
    // widget open; do not put on a hot path.
    info:    ()                          => request('POST', '/api/license/info'),
    // Synchronous KMS handshake; returns { result, license }.
    renew:   ()                          => request('POST', '/api/license/renew'),
  },

  ca: {
    info:    ()  => request('GET', '/api/ca'),
    crlInfo: ()  => request('GET', '/api/ca/crl'),
    caCertUrl: '/api/ca/cert.pem',
    crlPemUrl: '/api/ca/crl.pem',
    crlDerUrl: '/api/ca/crl.der',
  },

  certificates: {
    list:    (params = {})        => request('GET',    withQuery('/api/certificates', params)),
    get:     (id)                 => request('GET',    `/api/certificates/${id}`),
    issue:   (payload)            => request('POST',   '/api/certificates', payload),
    renew:   (id, payload = {})   => request('POST',   `/api/certificates/${id}/renew`, payload),
    revoke:  (id, reason)         => request('POST',   `/api/certificates/${id}/revoke`, { reason }),
    remove:  (id)                 => request('DELETE', `/api/certificates/${id}`),
    reasons: ()                   => request('GET',    '/api/certificates/reasons'),
    pemUrl:  (id)                 => `/api/certificates/${id}/cert.pem`,
  },

  nodes: {
    list:   (params = {})         => request('GET',    withQuery('/api/nodes', params)),
    get:    (id)                  => request('GET',    `/api/nodes/${id}`),
    create: (payload)             => request('POST',   '/api/nodes', payload),
    update: (id, patch)           => request('PATCH',  `/api/nodes/${id}`, patch),
    remove: (id)                  => request('DELETE', `/api/nodes/${id}`),
    bootstrapTokens: {
      list:   (nodeId)              => request('GET',    `/api/nodes/${nodeId}/bootstrap-tokens`),
      issue:  (nodeId, ttlMinutes)  => request('POST',   `/api/nodes/${nodeId}/bootstrap-tokens`, { ttlMinutes }),
      revoke: (nodeId, tokenId)     => request('DELETE', `/api/nodes/${nodeId}/bootstrap-tokens/${tokenId}`),
    },
  },

  units: {
    tree:   ()                    => request('GET',    '/api/units'),
    get:    (id)                  => request('GET',    `/api/units/${id}`),
    create: (payload)             => request('POST',   '/api/units', payload),
    update: (id, patch)           => request('PATCH',  `/api/units/${id}`, patch),
    move:   (id, parentId)        => request('POST',   `/api/units/${id}/move`, { parentId }),
    remove: (id)                  => request('DELETE', `/api/units/${id}`),
  },

  persons: {
    list:   (params = {})         => request('GET',    withQuery('/api/persons', params)),
    get:    (id)                  => request('GET',    `/api/persons/${id}`),
    create: (payload)             => request('POST',   '/api/persons', payload),
    update: (id, patch)           => request('PATCH',  `/api/persons/${id}`, patch),
    move:   (id, unitId)          => request('POST',   `/api/persons/${id}/move`, { unitId }),
    remove: (id)                  => request('DELETE', `/api/persons/${id}`),
    // Stage-3 — proxy to mesh microservice's /api/admin/enrollment-tickets.
    // `force: true` revokes any existing active terminal for this person
    // before minting the new ticket (§1.3). Without it, the backend
    // returns 409 with { existingTerminal } so the UI can confirm.
    enrollTerminal: (id, ttlMinutes, { force = false, validityDays } = {}) => {
      const body = { ttlMinutes };
      if (force) body.force = true;
      // 2026-07-05 (Round 1.450) — cert validity in days. Falls through
      // to server default (365d) when not supplied.
      if (validityDays != null) body.validityDays = validityDays;
      return request('POST', `/api/persons/${id}/enroll-terminal`, body);
    },
    // 2026-06-22 — coord-side terminals_registry surface for the
    // Revoke / Return actions on the Personnel page Actions column.
    listTerminals:    (id)                =>
      request('GET',  `/api/persons/${id}/terminals`),
    revokeTerminal:   (id, certSerial)    =>
      request('POST', `/api/persons/${id}/terminals/${certSerial}/revoke`),
    deleteTerminal:   (id, certSerial)    =>
      request('DELETE', `/api/persons/${id}/terminals/${certSerial}`),
    // 2026-07-05 (Round 1.450) — cert renewal without re-issue.
    // Re-signs the existing cert's public key with fresh notBefore /
    // notAfter, keeping the terminal's identity (uuid, pubkey,
    // keys, contacts, cache) fully intact. Body payload optional —
    // server clamps validityDays to config.ca.terminalCertTtlMaxDays.
    renewTerminal:    (id, certSerial, body = {}) =>
      request('POST', `/api/persons/${id}/terminals/${certSerial}/renew`, body),
  },

  oss: {
    exportUrl: '/api/oss/export',
    importDoc: (doc)              => request('POST',   '/api/oss/import', doc),
  },

  policy: {
    version:         ()           => request('GET',    '/api/policy/version'),
    snapshot:        ()           => request('GET',    '/api/policy/snapshot'),
    snapshotForNode: (nodeId)     => request('GET',    `/api/policy/snapshot/for-node/${nodeId}`),
    changes:         (limit)      => request('GET',    withQuery('/api/policy/changes', { limit })),
  },

  notifications: {
    inbox:        (params = {})   => request('GET',    withQuery('/api/notifications/inbox', params)),
    unreadCount:  ()              => request('GET',    '/api/notifications/inbox/unread-count'),
    markRead:     (id)            => request('POST',   `/api/notifications/inbox/${id}/read`),
    dismiss:      (id)            => request('POST',   `/api/notifications/inbox/${id}/dismiss`),
    markAllRead:  ()              => request('POST',   '/api/notifications/inbox/read-all'),
    list:         (params = {})   => request('GET',    withQuery('/api/notifications', params)),
    get:          (id)            => request('GET',    `/api/notifications/${id}`),
    create:       (payload)       => request('POST',   '/api/notifications', payload),
    retract:      (id)            => request('POST',   `/api/notifications/${id}/retract`),
    remove:       (id)            => request('DELETE', `/api/notifications/${id}`),
  },

  channels: {
    list:         (params = {})   => request('GET',    withQuery('/api/channels', params)),
    get:          (id)            => request('GET',    `/api/channels/${id}`),
    members:      (id)            => request('GET',    `/api/channels/${id}/members`),
    byUnit:       (unitId)        => request('GET',    `/api/channels/by-unit/${unitId}`),
    forPerson:    (personId)      => request('GET',    `/api/channels/for-person/${personId}`),
    createCustom: (payload)       => request('POST',   '/api/channels', payload),
    createForUnit:(unitId, payload) => request('POST', `/api/channels/oss/${unitId}`, payload),
    update:       (id, patch)     => request('PATCH',  `/api/channels/${id}`, patch),
    remove:       (id)            => request('DELETE', `/api/channels/${id}`),
    addMember:    (id, personId, role = 'writer') =>
                                     request('POST',   `/api/channels/${id}/members`, { personId, role }),
    setMemberRole:(id, personId, role) =>
                                     request('PATCH',  `/api/channels/${id}/members/${personId}`, { role }),
    removeMember: (id, personId)  => request('DELETE', `/api/channels/${id}/members/${personId}`),
  },

  // COP ADMS — proxied through /api/cop-proxy on this coord directly
  // to ad-cop-service. Surface mirrors the reference api.js shipped in
  // manuals/COP-microservice-integration/Datasets/api.js — every method
  // the operator's reference LayersTab page expects.
  cop: {
    // Cheap health probe — UI calls this once on page-mount to render a
    // reachable / not-installed banner.
    status: ()                                          => request('GET', '/api/cop-proxy/__status'),

    // ── COP Dataset Types (editable enum) ──────────────────────────────
    getDatasetTypes: ()                                 => request('GET', '/api/cop-proxy/cop-layers/dataset-types'),
    updateDatasetTypes: (types)                         => request('PUT', '/api/cop-proxy/cop-layers/dataset-types', { types }),

    // ── COP Datasets ──────────────────────────────────────────────────
    getDatasets: ()                                     => request('GET',    '/api/cop-proxy/cop-layers/datasets'),
    createDataset: (payload)                            => request('POST',   '/api/cop-proxy/cop-layers/datasets', payload),
    updateDataset: (id, payload)                        => request('PUT',    `/api/cop-proxy/cop-layers/datasets/${encodeURIComponent(id)}`, payload),
    deleteDataset: (id)                                 => request('DELETE', `/api/cop-proxy/cop-layers/datasets/${encodeURIComponent(id)}`),

    // ── COP Layers ────────────────────────────────────────────────────
    getLayers: ()                                       => request('GET',    '/api/cop-proxy/cop-layers'),
    getLayerStats: ()                                   => request('GET',    '/api/cop-proxy/cop-layers/stats'),
    getLayer: (id)                                      => request('GET',    `/api/cop-proxy/cop-layers/${encodeURIComponent(id)}`),
    createLayer: (payload)                              => request('POST',   '/api/cop-proxy/cop-layers', payload),
    updateLayer: (id, payload)                          => request('PUT',    `/api/cop-proxy/cop-layers/${encodeURIComponent(id)}`, payload),
    deleteLayer: (id)                                   => request('DELETE', `/api/cop-proxy/cop-layers/${encodeURIComponent(id)}`),
    deleteAllLayers: ()                                 => request('DELETE', '/api/cop-proxy/cop-layers/all'),
    cloneLayer: (id, payload)                           => request('POST',   `/api/cop-proxy/cop-layers/${encodeURIComponent(id)}/clone`, payload),

    // ── COP Layer Objects ─────────────────────────────────────────────
    getLayerObjects: (layerId)                          => request('GET',    `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/objects`),
    deleteLayerObject: (layerId, objId)                 => request('DELETE', `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/objects/${encodeURIComponent(objId)}`),

    // ── COP Layer Cache (SVG sidecar renderings) ──────────────────────
    deleteLayerCache: (layerId)                         => request('DELETE', `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/cache`),
    saveLayerCache: (layerId, items)                    => request('POST',   `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/cache`, { items }),
    renderLayerMilsymbol: (layerId)                     => request('POST',   `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/cache/milsymbol`),
    bakeLayerStyles: (layerId)                          => request('POST',   `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/styles/bake`),
    stripLayerStyles: (layerId)                         => request('DELETE', `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/styles`),

    // ── COP Layer Import / Export ─────────────────────────────────────
    // exportLayerUrl: returns the URL the browser can open directly
    // (window.open). Server-side proxy preserves Content-Disposition,
    // so the file downloads with the right name.
    exportLayerUrl: (layerId, format = 'full') =>
      `/api/cop-proxy/cop-layers/${encodeURIComponent(layerId)}/export?format=${encodeURIComponent(format)}`,

    importLayer: async (file, datasetId) => {
      const isZip = file.name.endsWith('.zip');
      const q = datasetId ? `?datasetId=${encodeURIComponent(datasetId)}` : '';
      const url = `/api/cop-proxy/cop-layers/import${q}`;
      // Bypass the regular request() helper — we need to send raw
      // bytes with application/zip Content-Type, not JSON. We still
      // hand-roll the CSRF token + auth-expired plumbing.
      let csrf;
      try {
        const t = await fetch('/api/csrf-token', { credentials: 'include' });
        csrf = (await t.json()).token;
      } catch { /* noop */ }
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Content-Type': isZip ? 'application/zip' : 'application/json',
          ...(csrf ? { 'x-csrf-token': csrf } : {}),
        },
        body: file,
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new ApiError(payload.error || `Import failed: ${res.status}`, res.status, payload);
      }
      return res.json();
    },

    importDeepstate: (json, datasetId) => {
      const q = datasetId ? `?datasetId=${encodeURIComponent(datasetId)}` : '';
      return request('POST', `/api/cop-proxy/cop-layers/import-deepstate${q}`, json);
    },

    // ── AWDS Layers (metoc) ───────────────────────────────────────────
    getAwdsLayers: ()                                   => request('GET',    '/api/cop-proxy/awds/layers'),
    getAwdsStatus: ()                                   => request('GET',    '/api/cop-proxy/awds/status'),
    getAwdsLayerFull: (id)                              => request('GET',    `/api/cop-proxy/awds/layers/${encodeURIComponent(id)}`),
    deleteAwdsLayer: (id)                               => request('DELETE', `/api/cop-proxy/awds/layers/${encodeURIComponent(id)}`),
    deleteAwdsArchiveLayers: ()                         => request('DELETE', '/api/cop-proxy/awds/layers?archive=true'),
    deleteAllAwdsLayers: ()                             => request('DELETE', '/api/cop-proxy/awds/layers'),
    syncAwds: ()                                        => request('POST',   '/api/cop-proxy/awds/sync'),
    exportAwdsLayerUrl: (id)                            => `/api/cop-proxy/awds/layers/${encodeURIComponent(id)}`,

    // ── Classifier helpers (used by client-side SVG render — v2) ──────
    getTypes: ()                                        => request('GET', '/api/cop-proxy/types'),
    getSymbolLayers: ()                                 => request('GET', '/api/cop-proxy/layers'),

    // ── NATO CoT Bridge ───────────────────────────────────────────────
    cot: {
      status: ()                                        => request('GET',  '/api/cop-proxy/nato/cot/status'),
      messages: (dir = 'rx')                            => request('GET',  withQuery('/api/cop-proxy/nato/cot/messages', { dir })),
      unmapped: ()                                      => request('GET',  '/api/cop-proxy/nato/cot/unmapped'),
      setRuntimeConfig: (patch)                         => request('POST', '/api/cop-proxy/nato/cot/runtime-config', patch),
      reconnectConnector: (peerId)                      => request('POST', `/api/cop-proxy/nato/cot/connectors/${encodeURIComponent(peerId)}/reconnect`),
    },

    // ── NATO NFFI Bridge ──────────────────────────────────────────────
    nffi: {
      status: ()                                        => request('GET',  '/api/cop-proxy/nato/nffi/status'),
      messages: (dir = 'rx')                            => request('GET',  withQuery('/api/cop-proxy/nato/nffi/messages', { dir })),
      unmapped: ()                                      => request('GET',  '/api/cop-proxy/nato/nffi/unmapped'),
      setRuntimeConfig: (patch)                         => request('POST', '/api/cop-proxy/nato/nffi/runtime-config', patch),
      pollNow: ()                                       => request('POST', '/api/cop-proxy/nato/nffi/poll-now'),
      pollProvider: (peerId)                            => request('POST', `/api/cop-proxy/nato/nffi/providers/${encodeURIComponent(peerId)}/poll`),
    },

    // ── NATO ADatP-3 Bridge + Orders ──────────────────────────────────
    adatp3: {
      status: ()                                        => request('GET',  '/api/cop-proxy/nato/adatp3/status'),
      messages: (filter = {})                           => request('GET',  withQuery('/api/cop-proxy/nato/adatp3/messages', filter)),
      message: (key)                                    => request('GET',  `/api/cop-proxy/nato/adatp3/messages/${encodeURIComponent(key)}`),
      upload: async (text) => {
        // Custom path — POST with Content-Type: text/plain. Hand-rolls
        // CSRF + auth-expired the same way importLayer does.
        let csrf;
        try {
          const t = await fetch('/api/csrf-token', { credentials: 'include' });
          csrf = (await t.json()).token;
        } catch { /* noop */ }
        const res = await fetch('/api/cop-proxy/nato/adatp3/upload', {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'Content-Type': 'text/plain',
            ...(csrf ? { 'x-csrf-token': csrf } : {}),
          },
          body: text,
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new ApiError(payload.error || `Upload failed: ${res.status}`, res.status, payload);
        }
        return res.json();
      },
      reparse: (key)                                    => request('POST',   `/api/cop-proxy/nato/adatp3/messages/${encodeURIComponent(key)}/reparse`),
      remove: (key)                                     => request('DELETE', `/api/cop-proxy/nato/adatp3/messages/${encodeURIComponent(key)}`),
      removeAll: ()                                     => request('DELETE', '/api/cop-proxy/nato/adatp3/messages'),
      acks: (key)                                       => request('GET',    `/api/cop-proxy/nato/adatp3/messages/${encodeURIComponent(key)}/acks`),
      acksSummary: ()                                   => request('GET',    '/api/cop-proxy/nato/adatp3/acks/summary'),
      getRouting: ()                                    => request('GET',    '/api/cop-proxy/nato/adatp3/routing'),
      updateRouting: (table)                            => request('PUT',    '/api/cop-proxy/nato/adatp3/routing', table),
    },
  },

  // Phase 2.5 step 1 — PoC live sessions (proxied through coord →
  // ad-tms-service /api/admin/poc/sessions). Read-only.
  // Phase 3.7 — Recordings browser (list / stream / delete).
  poc: {
    listSessions:    ()                                 => request('GET',    '/api/poc/sessions'),
    listRecordings:  (channelUuid)                      => request('GET',
      channelUuid ? `/api/poc/recordings?channelUuid=${encodeURIComponent(channelUuid)}`
                  : '/api/poc/recordings'),
    recordingUrl:    (channelUuid, id, download = false) =>
      `/api/poc/recordings/${encodeURIComponent(channelUuid)}/${encodeURIComponent(id)}`
        + (download ? '?download=1' : ''),
    deleteRecording: (channelUuid, id)                  => request('DELETE',
      `/api/poc/recordings/${encodeURIComponent(channelUuid)}/${encodeURIComponent(id)}`),

    // Phase 2.5+ — Mint a hidden-listener JWT for the coord admin
    // to silently monitor a PoC channel. Admin-role gated. Audit-
    // logged on the node side (start + stop).
    startListening:  (channelUuid)                      => request('POST',
      `/api/poc/listen/${encodeURIComponent(channelUuid)}`, {}),
  },
};

function withQuery(url, params) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.append(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${url}?${qs}` : url;
}

